import { computed, ref, onUnmounted } from 'vue'
import { toast } from '@rapoo/element'
import { storeToRefs } from 'pinia'
import { useConfigName } from '@/composables/useConfigName'
import { useDeviceStore, useConfigurationStore, useLocaleStore } from '@/stores'
import {
  configDelete,
  configFileUpload,
  configGet,
  configSave,
  OnlineConfig,
} from '@/service/api/user-config'
import { DeviceError, DeviceErrorCode } from '@rapoo/device-sdk'
import { defaultReportError } from '@/composables/useErrorLogger'

// 常量提取：便于统一维护
const CONSTS = {
  EVENTS: {
    UPLOAD_PROGRESS: 'config:Upload_current_config',
    DOWNLOAD_PROGRESS: 'config:Download_current_config',
  },
  MESSAGES: {
    UPLOAD_FAIL: 'toast.cloud.uploadFail',
    UPLOAD_SUCCESS: 'toast.cloud.uploadSuccess',
    LOGIN_EXPIRED_UPLOAD: 'toast.cloud.loginExpiredUpload',
    DOWNLOAD_FAIL: 'toast.cloud.downloadFail',
    DOWNLOAD_SUCCESS: 'toast.cloud.downloadSuccess',
    OPERATING: 'toast.cloud.operating',
    SELECT_CONFIG: 'toast.cloud.selectConfig',
    DELETE_FAIL: 'toast.cloud.deleteFail',
    DELETE_SUCCESS: 'toast.cloud.deleteSuccess',
  },
  DELAY: 10,
}

export function useConfigCloud() {
  // ==================== 基础 Store 初始化 ====================
  const { t } = useLocaleStore()
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const configStore = useConfigurationStore()
  const { currentConfig } = storeToRefs(configStore)

  // ==================== 通用状态 ====================
  const isReading = ref(false) // 防重复操作锁

  // ==================== 1. 上传配置模块 ====================
  const isUploadDialogOpen = ref(false)
  const configName = ref('')
  const deviceModel = computed(() => currentDevice.value?.manifest.model || '')
  const uploadProgress = ref(0)
  const isUploading = ref(false)

  // 通用进度监听封装
  const registerProgressListener = (eventName: string, callback: (progress: number) => void) => {
    currentDevice.value?.on(eventName, callback)
  }
  const unregisterProgressListener = (eventName: string, callback: (progress: number) => void) => {
    currentDevice.value?.off(eventName, callback)
  }

  // 上传进度回调
  const handleUploadProgress = (progress: number) => {
    uploadProgress.value = progress
  }

  const openUploadDialog = (_id: string) => {
    isUploadDialogOpen.value = true
    configName.value = useConfigName(currentConfig.value, currentDevice.value)
  }

  const closeUploadDialog = () => {
    isUploadDialogOpen.value = false
  }

  const closeUploadProgress = () => {
    isUploading.value = false
    unregisterProgressListener(CONSTS.EVENTS.UPLOAD_PROGRESS, handleUploadProgress)
  }

  const handleUploadConfig = async () => {
    const config = currentDevice.value?.features.config
    if (!config) {
      toast.error(t(CONSTS.MESSAGES.UPLOAD_FAIL))
      return
    }

    // 防重复提交
    if (isReading.value) {
      toast.info(t(CONSTS.MESSAGES.OPERATING))
      isUploading.value = true
      return
    }

    try {
      isReading.value = true
      isUploading.value = true
      uploadProgress.value = 0

      // 先取消旧监听，再注册新监听
      unregisterProgressListener(CONSTS.EVENTS.UPLOAD_PROGRESS, handleUploadProgress)
      registerProgressListener(CONSTS.EVENTS.UPLOAD_PROGRESS, handleUploadProgress)

      // 获取配置快照并上传
      const res = await config.getCurrentConfigSnapshot()
      const file = new File([JSON.stringify(res)], 'config.json', {
        type: 'application/json',
      })
      const { data } = await configFileUpload(file)

      if (data.filePath) {
        const saveResult = await configSave({
          filePath: data.filePath,
          configType: deviceModel.value,
          name: configName.value,
          configContent: '',
        })

        if (saveResult.code === 10011) {
          closeUploadProgress()
          toast.error(t(CONSTS.MESSAGES.LOGIN_EXPIRED_UPLOAD))
          return
        }

        toast.success(t(CONSTS.MESSAGES.UPLOAD_SUCCESS))
        closeUploadDialog()
      }
    } catch (error) {
      console.error('Upload config error:', error)
      toast.error(t(CONSTS.MESSAGES.UPLOAD_FAIL))
      defaultReportError(`Upload config error: ${error}`)
    } finally {
      isUploading.value = false
      unregisterProgressListener(CONSTS.EVENTS.UPLOAD_PROGRESS, handleUploadProgress)
      setTimeout(() => {
        isReading.value = false
      }, CONSTS.DELAY)
    }
  }

  // ==================== 2. 配置列表模块 ====================
  const isConfigListDialogOpen = ref(false)
  const configList = ref<OnlineConfig[]>([]) // 修复拼写错误 configtList → configList
  const deviceIcon = computed(() => currentDevice.value?.manifest.ui.icon || '')

  const openConfigListDialog = (_id: string) => {
    isConfigListDialogOpen.value = true
    configItem.value = null // 初始化改为null，更规范
    handleConfigList()
  }

  const closeConfigListDialog = () => {
    isConfigListDialogOpen.value = false
  }

  const handleConfigList = async () => {
    try {
      const res = await configGet({ configType: deviceModel.value })
      configList.value = res.data ?? []
      configItem.value = configList.value[0] ?? null
    } catch (error) {
      console.error('Get config list error:', error)
      defaultReportError(`Get config list error: ${JSON.stringify(error)}`)
      configList.value = []
    }
  }

  // ==================== 3. 下载配置模块 ====================
  const configItem = ref<OnlineConfig | null>(null) // 类型优化：允许null
  const isDownloading = ref(false)
  const downloadProgress = ref(0)

  // 下载进度回调
  const handleDownloadProgress = (progress: number) => {
    downloadProgress.value = progress
  }

  const selectConfigItem = (item: OnlineConfig) => {
    configItem.value = item
  }

  const handleDownloadConfig = async () => {
    if (!configItem.value?.filePath) {
      toast.warning(t(CONSTS.MESSAGES.SELECT_CONFIG))
      return
    }

    isDownloading.value = true
    if (isReading.value) {
      toast.info(t(CONSTS.MESSAGES.OPERATING))
      return
    }

    try {
      isReading.value = true
      downloadProgress.value = 0

      // 进度监听处理
      unregisterProgressListener(CONSTS.EVENTS.DOWNLOAD_PROGRESS, handleDownloadProgress)
      registerProgressListener(CONSTS.EVENTS.DOWNLOAD_PROGRESS, handleDownloadProgress)

      // 构建代理URL
      const originalUrl = new URL(configItem.value.filePath)
      const baseUrl = import.meta.env.VITE_API_SOURCE_FILE
      const proxyUrl = `${baseUrl}${originalUrl.pathname}`

      const res = await fetch(proxyUrl)
      if (!res.ok) throw new Error('Fetch config file failed')
      const json = await res.json()

      const config = currentDevice.value?.features.config
      if (!config) {
        throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, 'Invalid device')
      }

      await config.setCurrentConfigSnapshot(json)
      toast.success(t(CONSTS.MESSAGES.DOWNLOAD_SUCCESS))
    } catch (error) {
      console.error('Download config error:', error)
      toast.error(t(CONSTS.MESSAGES.DOWNLOAD_FAIL))
      defaultReportError(`Download config error: ${JSON.stringify(error)}`)
    } finally {
      isDownloading.value = false
      unregisterProgressListener(CONSTS.EVENTS.DOWNLOAD_PROGRESS, handleDownloadProgress)
      setTimeout(() => {
        isReading.value = false
      }, CONSTS.DELAY)
    }
  }

  // ==================== 4. 删除配置模块 ====================
  const openDelLoadDialog = async (item: OnlineConfig, e: Event, close: () => void) => {
    e.stopPropagation() // 阻止事件冒泡
    close()

    if (!item.id) {
      toast.error(t(CONSTS.MESSAGES.DELETE_FAIL))
      return
    }

    try {
      await configDelete(item.id)
      toast.success(t(CONSTS.MESSAGES.DELETE_SUCCESS))
      configList.value = configList.value.filter((cfg) => cfg.id !== item.id)
      // 若删除的是当前选中项，重置选中状态
      if (configItem.value?.id === item.id) {
        configItem.value = configList.value[0] || null
      }
    } catch (error) {
      console.error('Delete config error:', error)
      toast.error(t(CONSTS.MESSAGES.DELETE_FAIL))
      defaultReportError(`Delete config error: ${JSON.stringify(error)}`)
    }
  }

  // ==================== 资源清理：组件卸载时取消所有监听 ====================
  onUnmounted(() => {
    unregisterProgressListener(CONSTS.EVENTS.UPLOAD_PROGRESS, handleUploadProgress)
    unregisterProgressListener(CONSTS.EVENTS.DOWNLOAD_PROGRESS, handleDownloadProgress)
  })

  // ==================== 导出 ====================
  return {
    // 基础数据
    configName,
    deviceModel,
    // 上传模块
    isUploadDialogOpen,
    uploadProgress,
    isUploading,
    openUploadDialog,
    closeUploadDialog,
    handleUploadConfig,
    // 列表模块
    isConfigListDialogOpen,
    configList, // 同步拼写修正
    deviceIcon,
    openConfigListDialog,
    closeConfigListDialog,
    handleConfigList,
    // 下载模块
    configItem,
    isDownloading,
    downloadProgress,
    selectConfigItem,
    handleDownloadConfig,
    // 删除模块
    openDelLoadDialog,
  }
}
